import { useEffect, useState } from 'react';
import cn from 'classnames';

import SingleFilter from '../Filter/SingleFilter';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { updateFilter, fetchFilterByMotionType } from '../../features/filter/filterSlice';
import useTranslation from '../../features/translation/hooks/useTranslation';
import { filterPreparation, filterValuePreparation } from './utils';
import { FilterBase } from '../../interfaces';
import { FilterValueName } from '../../features/filter/interfaces';
import './Parameters.scss';
import { econdaClickEvent } from '../../Econda';

enum TranslationKeys {
  ENTRY_PAGE_INSTRUCTIONS = 'entryPageInstructions',
}

export enum ColumnWidth {
  SIZE_33 = 4,
  SIZE_25 = 3,
  SIZE_50 = 6,
  SIZE_100 = 12,
}

export default function Parameters(): JSX.Element {
  const dispatch = useAppDispatch();
  const {
    filters: storedFilters,
    sectionFilters: filterList,
    resultFilters: resultsFilterList,
    isFiltersLoading,
    isResultsLoading,
  } = useAppSelector((state) => state.filters);
  const { motionType } = storedFilters;
  const t = useTranslation();

  const [loadingFilters, setLoadingFilters] = useState(true);

  let newRow = false;

  const handleChange = (filterName: string, value: (string | number)[] | string | number): void => {
    let multiValue: (string | number)[] = [];
    if (Array.isArray(value)) {
      multiValue = value;
    } else {
      multiValue.push(value);
    }

    const isLubricated = multiValue.length > 0;
    econdaClickEvent(
      'application parameters',
      'calculation filter',
      `${filterName}`,
      `${multiValue.length > 0 ? multiValue.join(', ') : value}`,
    );

    if (filterName === 'condition') {
      dispatch(
        updateFilter({
          name: 'condition',
          newValue: isLubricated,
        }),
      );
    }

    if (Array.isArray(value)) return;

    if (filterName === 'materialOne' || filterName === 'materialTwo') {
      dispatch(
        updateFilter({
          name: filterName,
          newValue: value,
        }),
      );
    } else {
      dispatch(
        updateFilter({
          name: filterName,
          newValue: +value,
        }),
      );
    }
  };

  useEffect(() => {
    const fetchFilters = async () => await dispatch(fetchFilterByMotionType(motionType));

    if (filterList.length < 1 || resultsFilterList.length < 1) fetchFilters();
  }, [dispatch, filterList.length, motionType, resultsFilterList.length]);

  useEffect(() => {
    setLoadingFilters(true);

    const allFilters = [...filterList, ...resultsFilterList];
    const isFirstTimeLoadingFilters =
      isFiltersLoading === 'success' && (isResultsLoading === 'pending' || isResultsLoading === 'idle');

    if (isFirstTimeLoadingFilters) {
      allFilters.forEach((fil) => {
        const defaultFilterValue = filterValuePreparation(fil);

        if (defaultFilterValue !== null && defaultFilterValue !== undefined) {
          if (Array.isArray(defaultFilterValue)) {
            // cushioningReaction is array of objects
            dispatch(
              updateFilter({
                name: defaultFilterValue[0].name,
                newValue: defaultFilterValue[0].newValue,
              }),
            );
            dispatch(
              updateFilter({
                name: defaultFilterValue[1].name,
                newValue: defaultFilterValue[1].newValue,
              }),
            );
          } else {
            dispatch(
              updateFilter({
                name: defaultFilterValue.name,
                newValue: defaultFilterValue.newValue,
              }),
            );
          }
        }
      });
    }

    setLoadingFilters(false);
  }, [dispatch, filterList, resultsFilterList, isFiltersLoading, isResultsLoading]);

  return (
    <div className="ems-parameters-container">
      <p className="ems-entry-page-instructions">{t(TranslationKeys.ENTRY_PAGE_INSTRUCTIONS)}</p>
      <div className="fwe-grid ems-parameters">
        {filterList &&
          !loadingFilters &&
          filterList.length > 0 &&
          filterList.map((singleFilter, index) => {
            const colNumber = ColumnWidth.SIZE_33;

            const storedValue = storedFilters[singleFilter.key as FilterValueName];

            if (typeof storedValue !== 'string' && typeof storedValue !== 'number') return null;

            const preparedFilter = filterPreparation(singleFilter, storedValue, t);

            const content = (
              <div
                key={singleFilter.id}
                className={cn(`fwe-col-${colNumber} ems-filter-${colNumber}`, {
                  'ems-filter--first': newRow,
                })}
              >
                <SingleFilter filter={preparedFilter as FilterBase} onChange={handleChange} />
              </div>
            );
            newRow = (index + 1) % 2 === 0;

            return content;
          })}
      </div>
    </div>
  );
}
